import React from 'react';
import LogoImage from '../../../Assets/kwikmediaLogo.webp';
import { CANONICAL_SITE_URL } from '../../../config/canonicalSite';
import {
    LegalPageRoot,
    LegalCard,
    LegalBrandRow,
    LegalLogo,
    LegalDocTitle,
    LegalDocMeta,
    LegalBody,
    LegalSectionHeading,
    LegalParagraph,
    LegalOrderedList,
    LegalList,
    LegalListItem,
    LegalNestedList,
    LegalLink,
} from './LegalDocumentStyles';

const TermsCondition = () => {
    return (
        <LegalPageRoot>
            <LegalCard>
                <LegalBrandRow>
                    <LegalLogo src={LogoImage} alt="KwikMemories" />
                    <LegalDocTitle>Terms and Conditions</LegalDocTitle>
                    <LegalDocMeta>kwikmemories.com</LegalDocMeta>
                </LegalBrandRow>

                <LegalBody>
                    <LegalSectionHeading>1. Acceptance of Terms</LegalSectionHeading>
                    <LegalParagraph>
                        By accessing and using{' '}
                        <LegalLink href={`${CANONICAL_SITE_URL}`}
                            target="_parent"
                            rel="noopener noreferrer">kwikmemories.com</LegalLink> (&quot;Website&quot;),
                        you agree to comply with and be bound by the following terms and conditions
                        (&quot;Terms&quot;). If you do not agree to these Terms, please do not use our Website.
                    </LegalParagraph>

                    <LegalSectionHeading>2. Use of the Website</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>Eligibility:</strong> You must be at least 18 years old or have reached the
                            age of majority in your jurisdiction to use this Website. By using this Website, you
                            represent and warrant that you meet these eligibility requirements.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>License to Use:</strong> We grant you a limited, non-exclusive,
                            non-transferable, and revocable license to access and use our Website for personal and
                            commercial purposes, subject to these Terms.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Prohibited Conduct:</strong> You agree not to engage in any of the following
                            activities:
                            <LegalNestedList>
                                <LegalListItem>
                                    Using the Website for any illegal or unauthorized purpose.
                                </LegalListItem>
                                <LegalListItem>
                                    Posting or transmitting any content that is unlawful, harmful, threatening,
                                    abusive, defamatory, vulgar, obscene, or otherwise objectionable.
                                </LegalListItem>
                                <LegalListItem>
                                    Attempting to interfere with or disrupt the operation of the Website, its
                                    servers, or networks connected to it.
                                </LegalListItem>
                                <LegalListItem>
                                    Using any automated system, including &quot;robots,&quot; &quot;spiders,&quot; or
                                    &quot;offline readers,&quot; to access the Website in a manner that sends more
                                    request messages to our servers than a human could reasonably produce in the same
                                    period of time.
                                </LegalListItem>
                            </LegalNestedList>
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>3. User Accounts</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>Account Creation:</strong> To access certain features of the Website, you may
                            be required to create an account. You agree to provide accurate, complete, and
                            up-to-date information when creating your account.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Account Security:</strong> You are responsible for maintaining the
                            confidentiality of your account credentials and for all activities that occur under
                            your account. You agree to notify us immediately of any unauthorized use of your
                            account.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Account Termination:</strong> We reserve the right to terminate your account at
                            our sole discretion if we believe you have violated these Terms or engaged in
                            fraudulent or harmful activities.
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>4. Intellectual Property</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>Ownership:</strong> All content, materials, and intellectual property on the
                            Website, including but not limited to text, graphics, logos, images, and software, are
                            the property of kwikmemories or its licensors and are protected by copyright,
                            trademark, and other intellectual property laws.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Limited Use:</strong> You may not copy, reproduce, distribute, or create
                            derivative works of any content from the Website without our prior written consent.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Trademarks:</strong> All trademarks, service marks, and logos used on the
                            Website are the trademarks of kwikmemories or their respective owners. You may not
                            use any trademarks without the prior written consent of the owner.
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>5. Products and Services</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>Availability:</strong> All products and services offered on the Website are
                            subject to availability and may be withdrawn or modified at any time without notice.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Pricing:</strong> We reserve the right to change the prices of our products and
                            services at any time. Prices for products are listed in [Currency] and include
                            applicable taxes unless otherwise stated.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Orders:</strong> By placing an order on the Website, you agree to pay the total
                            amount for the order, including any applicable taxes and shipping fees. We reserve the
                            right to refuse or cancel any order at our discretion.
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>6. Payment Terms</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>Payment Methods:</strong> We accept various payment methods, including [list
                            accepted payment methods, e.g., credit cards, PayPal, etc.]. Payments are processed
                            through secure third-party payment processors.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Billing Information:</strong> You agree to provide accurate and complete billing
                            information. If we cannot process your payment, we may cancel your order.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Refunds and Returns:</strong> Please refer to our{' '}
                            <LegalLink
                                href={`${CANONICAL_SITE_URL}/extra/refund-register`}
                                target="_parent"
                                rel="noopener noreferrer"
                            >
                                Refund and Registration Policy
                            </LegalLink>{' '}
                            for
                            detailed information on refunds and returns.
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>7. Third-Party Links</LegalSectionHeading>
                    <LegalParagraph>
                        The Website may contain links to third-party websites or services that are not owned or
                        controlled by kwikmemories. We have no control over, and assume no responsibility for,
                        the content, privacy policies, or practices of any third-party websites or services.
                        We strongly advise you to read the terms and conditions and privacy policies of any
                        third-party websites or services that you visit.
                    </LegalParagraph>

                    <LegalSectionHeading>8. Disclaimers and Limitation of Liability</LegalSectionHeading>
                    <LegalOrderedList>
                        <LegalListItem>
                            <strong>No Warranties:</strong> The Website is provided on an &quot;as is&quot; and
                            &quot;as available&quot; basis without any warranties, express or implied. We do not
                            warrant that the Website will be uninterrupted, error-free, or secure.
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Limitation of Liability:</strong> To the fullest extent permitted by law,
                            kwikmemories and its affiliates, directors, employees, and agents will not be liable
                            for any direct, indirect, incidental, special, or consequential damages arising out of or
                            in connection with your use of the Website, including but not limited to damages for
                            loss of profits, data, or goodwill.
                        </LegalListItem>
                    </LegalOrderedList>

                    <LegalSectionHeading>9. Indemnification</LegalSectionHeading>
                    <LegalParagraph>
                        You agree to indemnify, defend, and hold harmless kwikmemories and its affiliates,
                        directors, employees, and agents from and against any claims, liabilities, damages,
                        losses, and expenses, including reasonable attorney&apos;s fees, arising out of or in any
                        way connected with your use of the Website, your violation of these Terms, or your
                        violation of any rights of another.
                    </LegalParagraph>

                    <LegalSectionHeading>10. Governing Law</LegalSectionHeading>
                    <LegalParagraph>
                        These Terms and your use of the Website will be governed by and construed in accordance
                        with the laws of [Your Jurisdiction], without regard to its conflict of laws principles.
                        You agree to submit to the exclusive jurisdiction of the courts located in [Your
                        Jurisdiction] for the resolution of any disputes.
                    </LegalParagraph>

                    <LegalSectionHeading>11. Changes to These Terms</LegalSectionHeading>
                    <LegalParagraph>
                        We may update these Terms from time to time to reflect changes in our practices or legal
                        requirements. The updated Terms will be posted on this page with the &quot;Effective
                        Date&quot; updated accordingly. We encourage you to review these Terms periodically.
                    </LegalParagraph>

                    <LegalSectionHeading>12. Contact Us</LegalSectionHeading>
                    <LegalParagraph>
                        If you have any questions or concerns about these Terms, please contact us at:
                    </LegalParagraph>
                    <LegalList>
                        <LegalListItem>
                            <strong>Email:</strong>{' '}
                            <LegalLink href="mailto:contact@kwikmemories.com">contact@kwikmemories.com</LegalLink>
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Phone:</strong>{' '}
                            <LegalLink href="tel:+919875082248">+91 9875082248</LegalLink>
                        </LegalListItem>
                        <LegalListItem>
                            <strong>Address:</strong> Ventureboo LLP, Surat - 395010
                        </LegalListItem>
                    </LegalList>
                </LegalBody>
            </LegalCard>
        </LegalPageRoot>
    );
};

export default TermsCondition;
